/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable no-param-reassign */
import React, {
  useState,
  useEffect,
  useCallback,
  Dispatch,
  SetStateAction,
} from 'react';
import {
  Box,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
} from '@material-ui/core';
import { Pagination } from '@material-ui/lab';
import Swal from 'sweetalert2';
import { useQuery, useMutation } from '@apollo/client';
import { AddCircle, Delete } from '@material-ui/icons';
import BlocksProvider from 'store/blocks.context';
import useStyles from './styles';
import {
  GET_BLOCKS_AND_TOTAL_BY_TITLE,
  CREATE_BLOCKS,
  DELETE_BLOCK,
  UPDATE_BLOCK,
  UPDATE_BLOCK_PRIORITY,
} from '../../queries/blocks';
import BlocksAccordion from './BlocksAccordion';
import Dropzone from '../Dropzone/Dropzone';
import Loading from '../Loading';
import { Toast } from '../Toast';

interface IBlocksContentsProps {
  content: string;
  setContent: Dispatch<SetStateAction<string>>;
  titleId: string;
  siteDomain: string;
  isSavingContent: boolean;
  isAutoSaving: boolean;
  autoSavingText: string;
  dynamicFieldsStringified: string;
  showDynamicFields: boolean;
}

export interface ITreatedFile {
  fileContent: string;
  fileName: string;
}

export interface IUnsavedBlock {
  titleId: string;
  subtitle: string;
  content: string;
  priority: number;
}

export type Blocks = (IUnsavedBlock | IBlock)[];

function isBlock(block: IUnsavedBlock | IBlock): block is IBlock {
  if ((block as IBlock).id) {
    return true;
  }

  return false;
}

export const BlocksContents: React.FC<IBlocksContentsProps> = ({
  content,
  setContent,
  titleId,
  siteDomain,
  isSavingContent,
  isAutoSaving,
  autoSavingText,
  dynamicFieldsStringified,
  showDynamicFields,
}) => {
  const classes = useStyles();

  const [itemsSelected] = useState(new Map());
  const [totalSelected, setTotalSelected] = useState(0);

  const [blocks, setBlocks] = useState<IBlock[]>([]);
  const [unsavedBlocks, setUnsavedBlocks] = useState<IBlock[]>([]);

  const [page, setPage] = useState(1);
  const [offset, setOffset] = useState(0);
  const [pagesTotal, setPagesTotal] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  const [blocksToAdd, setBlocksToAdd] = useState<ITreatedFile[]>([]);
  const [loadingOpen, setLoadingOpen] = useState(false);

  const queryBlocks = useQuery(GET_BLOCKS_AND_TOTAL_BY_TITLE, {
    variables: {
      titleId,
      offset,
      limit: rowsPerPage,
    },
    fetchPolicy: 'network-only',
  });

  const [createBlocksMutation] = useMutation(CREATE_BLOCKS);
  const [updateBlock] = useMutation(UPDATE_BLOCK);
  const [deleteBlock] = useMutation(DELETE_BLOCK);
  const [updateBlockPriority] = useMutation(UPDATE_BLOCK_PRIORITY);

  const createBlocksFromFiles = (
    files: ITreatedFile[],
    titleId: string,
  ): IUnsavedBlock[] => {
    return files.map((f) => {
      return {
        titleId,
        subtitle: f.fileName,
        content: f.fileContent,
        priority: 0,
      };
    });
  };

  const setRows = useCallback((value) => {
    setLoadingOpen(true);
    setRowsPerPage(value);
  }, []);

  const removeFromUnsaved = useCallback(
    (blocks) => {
      if (blocks.length > 0) {
        setUnsavedBlocks(
          unsavedBlocks.filter(
            (unsaved) =>
              !blocks.find((block: any) => unsaved.subtitle === block.subtitle),
          ),
        );
      }
    },
    [setUnsavedBlocks, unsavedBlocks],
  );

  const saveBlocks = useCallback(
    async (blocks: Blocks, alert = false) => {
      try {
        blocks.map(async (block) => {
          if (isBlock(block)) {
            updateBlock({
              variables: {
                id: block.id,
                titleId: block.titleId,
                subtitle: block.subtitle,
                content: block.content,
                priority: block.priority,
                productId: block.productId,
                brandId: block.brandId,
              },
            });
          }
        });

        const newBlocks = blocks.filter(
          (block) => (block as IBlock).id === undefined,
        );
        if (newBlocks.length > 0) {
          const {
            data: { createBlock: blocksCreated },
          } = await createBlocksMutation({ variables: { input: newBlocks } });
          removeFromUnsaved(blocksCreated);
        }

        const {
          data: {
            blocksAndTotalByTitle: { total },
          },
        } = await queryBlocks.refetch();
        if (total > 5 && newBlocks.length > 0) {
          setPage(Math.ceil(total / rowsPerPage));
        } else if (newBlocks.length > 0) {
          setPage(1);
        }
        if (alert) {
          Toast('Block(s) saved successfully!');
        }
      } catch (error) {
        if (alert) {
          Toast('Failed to save block(s)!', 'error');
        }
      }
    },
    [
      createBlocksMutation,
      queryBlocks,
      rowsPerPage,
      updateBlock,
      removeFromUnsaved,
    ],
  );

  useEffect(() => {
    if (page > 0) {
      setOffset((page - 1) * rowsPerPage);
    }
  }, [page, rowsPerPage]);

  // TODO
  useEffect(() => {
    if (isSavingContent) {
      saveBlocks(blocks);
    }
  }, [isSavingContent, saveBlocks, blocks]);

  // TODO
  useEffect(() => {
    if (blocksToAdd.length > 0) {
      saveBlocks([...blocks, ...createBlocksFromFiles(blocksToAdd, titleId)]);
      setBlocksToAdd([]);
    }
  }, [blocksToAdd, blocks, saveBlocks, titleId]);

  // TODO
  useEffect(() => {
    if (queryBlocks && queryBlocks.data) {
      setLoadingOpen(true);
      const {
        data: {
          blocksAndTotalByTitle: { blocks, total },
        },
      } = queryBlocks;
      setBlocks([...blocks, ...unsavedBlocks]);
      const totalPages = Math.ceil(total / rowsPerPage);
      setPagesTotal(totalPages);
      if (page > totalPages) {
        setPage(totalPages);
      }
      if (total > 0) {
        setTimeout(() => {
          setLoadingOpen(false);
        }, 1500);
      } else {
        setLoadingOpen(false);
      }
    }
  }, [queryBlocks, page, rowsPerPage, setBlocks, unsavedBlocks]);

  const handleSortEnd = useCallback(
    async ({ oldIndex, newIndex }) => {
      oldIndex -= 1;
      newIndex -= 1;
      if (
        oldIndex === newIndex ||
        blocks[oldIndex] === undefined ||
        blocks[newIndex] === undefined
      ) {
        return;
      }
      if (!blocks[oldIndex].id || !blocks[newIndex].id) return;

      setLoadingOpen(true);
      const oldNode = blocks[oldIndex];
      const newNode = blocks[newIndex];
      const result = await updateBlockPriority({
        variables: {
          titleId,
          blockId: oldNode.id,
          blockIdReplaced: newNode.id,
        },
      });
      if (result && result.data && result.data.updateBlockPriority) {
        setUnsavedBlocks(blocks.filter((b) => !b.id));
        await queryBlocks.refetch();
        setLoadingOpen(false);
      }
    },
    [blocks, titleId, queryBlocks, updateBlockPriority],
  );

  const addNewBlankBlock = () => {
    if (!blocks.some((b) => b.subtitle === 'New block' && !b.id)) {
      const aux: Blocks = [...blocks];
      aux.push({
        titleId,
        subtitle: 'New block',
        content: '',
        priority: 0,
      });
      setBlocks(aux as any);
      Toast('Block created successfully!');
    } else {
      Toast(
        'You still have pending blocks! Save them to continue...',
        'warning',
      );
    }
  };

  const deleteBlocks = useCallback(
    async (removeBlocks) => {
      const result = await Swal.fire({
        title: 'Are you sure?',
        text: 'Are you sure that you want to delete this block?',
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: 'Confirm',
        customClass: {
          container: classes.swalDialog,
        },
        confirmButtonColor: '#78001b',
        reverseButtons: true,
      });
      if (result.value) {
        setLoadingOpen(true);
        try {
          await removeBlocks();
          Toast('Block(s) removed successfully!');
        } catch (error) {
          Toast('Failed to remove block(s)!', 'error');
        } finally {
          await queryBlocks.refetch();
          setTotalSelected(itemsSelected.size);
        }
      }
    },
    [itemsSelected.size, queryBlocks],
  );

  const handleDeleteBlock = useCallback(
    async (id) => {
      await deleteBlocks(() => deleteBlock({ variables: { id } }));
    },
    [deleteBlock, deleteBlocks],
  );

  const handleChangePage = async (page: number) => {
    setUnsavedBlocks(blocks.filter((b) => !b.id));
    setPage(page);
  };

  const deleteSelectedBlocks = async () => {
    for (const [key] of Array.from(itemsSelected)) {
      // eslint-disable-next-line no-await-in-loop
      await deleteBlock({ variables: { id: key } });
      itemsSelected.delete(key);
    }
  };

  const shouldCancelStart = useCallback((e) => {
    if (['select-brand-block', 'select-product-block'].includes(e.target.id)) {
      return true;
    }
    return false;
  }, []);

  return (
    <BlocksProvider
      value={{
        blocks,
        setBlocks,
        unsavedBlocks,
        setUnsavedBlocks,
        siteDomain,
        dynamicFieldsList: dynamicFieldsStringified,
        dynamicFieldsPermission: showDynamicFields,
      }}
    >
      <Loading openLoading={loadingOpen} dismiss={false} />
      <Box display="flex" mb={2}>
        <Dropzone setTreatedFiles={setBlocksToAdd} siteDomain={siteDomain} />
      </Box>
      <Box display="flex" mb={2}>
        <Box flexGrow={1}>
          <Button
            onClick={() => addNewBlankBlock()}
            className={classes.button}
            variant="contained"
            color="primary"
          >
            <AddCircle />
            <Typography className={classes.spaceIconText}>
              Add new block
            </Typography>
          </Button>
          {totalSelected > 0 && (
            <Button
              className={classes.button}
              variant="outlined"
              onClick={() => deleteBlocks(deleteSelectedBlocks)}
              color="primary"
            >
              <Delete />
              <Typography className={classes.spaceIconText}>Delete</Typography>
            </Button>
          )}
        </Box>
        <Box display="flex">
          <Pagination
            count={pagesTotal}
            color="primary"
            onChange={(e, page) => handleChangePage(page)}
            page={page}
          />
          {pagesTotal > 5 && (
            <FormControl className={classes.formControl} variant="filled">
              <InputLabel id="demo-simple-select-label">Page</InputLabel>
              <Select
                labelId="demo-simple-select-label"
                id="demo-simple-select"
                value={page}
                onChange={(e) => setPage(e.target.value as number)}
              >
                {Array.from(Array(pagesTotal), (_, i) => i + 1).map((i) => {
                  return (
                    <MenuItem key={i} value={i}>
                      {i}
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
          )}
        </Box>
      </Box>
      <Box p={1}>
        {isAutoSaving && (
          <Typography component="p" className={classes.autosaveLoading}>
            {autoSavingText}
          </Typography>
        )}
      </Box>
      <BlocksAccordion
        setContent={setContent}
        content={content}
        onSortEnd={handleSortEnd}
        setTotalSelected={setTotalSelected}
        itemsSelected={itemsSelected}
        pressDelay={200}
        pressThreshold={200}
        saveBlocks={saveBlocks}
        deleteBlock={handleDeleteBlock}
        rowsPerPage={rowsPerPage}
        setRowsPerPage={setRows}
        helperClass={classes.sortableHelper}
        shouldCancelStart={shouldCancelStart}
      />
    </BlocksProvider>
  );
};
